import React, { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Ticket,
  Users,
  DollarSign,
  Settings,
  LogOut,
  Plus,
  Trash2,
  Check,
  Clock,
  ChevronDown,
  Search,
  X,
  QrCode,
  Camera,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Edit,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  FormInput,
  Calendar,
  Mail,
  Send,
  Eye,
  Menu,
  Paperclip,
  Wrench,
  ExternalLink,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Checkbox } from "../../components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../context/AuthContext";
import { Html5Qrcode } from "html5-qrcode";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SessionManagement, CouponManagement, CustomFieldsManagement } from "../../components/AdminSettings";
import RichTextEditor from "../../components/RichTextEditor";

import { API_URL } from "../../config";
import { EditSaleModal } from "./EditSaleModal";
import { SortableHeader } from "./shared/SortableHeader";
import { exportToPDF, exportToExcel } from "../../lib/export";
import { suggestEmailFix } from "../../lib/emailTypo";
import { ColumnPickerModal } from "../../components/admin/ColumnPickerModal";
import { RESERVED_CORE_FIELD_NAMES } from "../../lib/reservedFields";
import { isPendingStatus } from "../../lib/paymentStatus";


// ────────────────────────────────────────────────────────────────────
// Minor parental authorization dialog — shows declaration data + PDF
// ────────────────────────────────────────────────────────────────────
const MinorParentalDialog = ({ sale, onClose, onRefresh }) => {
  const open = !!sale;
  const [resending, setResending] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [showAudit, setShowAudit] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({ minor: {}, guardian: {}, responsible: {} });

  React.useEffect(() => {
    if (sale?.parental_auth) {
      const a = sale.parental_auth;
      setForm({
        minor: { full_name: a.minor?.full_name || "", dob: a.minor?.dob || "" },
        guardian: {
          full_name: a.guardian?.full_name || "",
          id_doc: a.guardian?.id_doc || "",
          contact: a.guardian?.contact || "",
          whatsapp: a.guardian?.whatsapp || "",
          email: a.guardian?.email || "",
          address: a.guardian?.address || "",
        },
        responsible: {
          full_name: a.responsible?.full_name || "",
          contact: a.responsible?.contact || "",
          whatsapp: a.responsible?.whatsapp || "",
          email: a.responsible?.email || "",
        },
      });
      setEditing(false);
      setShowAudit(false);
    }
  }, [sale?.id, sale?.parental_auth?.audit_log?.length]);

  if (!sale) return null;
  const auth = sale.parental_auth || {};
  const minor = auth.minor || {};
  const guardian = auth.guardian || {};
  const responsible = auth.responsible || {};
  const acceptance = auth.acceptance || {};
  const auditLog = Array.isArray(auth.audit_log) ? auth.audit_log : [];
  const isU12 = minor.category === "child_under_12";
  const roleLbl = isU12 ? "Acompanhante" : "Responsável";

  const setField = (block, field, value) =>
    setForm((p) => ({ ...p, [block]: { ...p[block], [field]: value } }));

  const downloadPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations/${sale.id}/parental-pdf`;
    window.open(url, "_blank", "noopener");
  };
  const resend = async () => {
    setResending(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations/${sale.id}/resend-parental-email`,
        {},
        { withCredentials: true }
      );
      if (data.sent) toast.success("Termo reenviado por email.");
      else toast.warning("Email não pôde ser enviado (verifique Resend).");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao reenviar.");
    } finally {
      setResending(false);
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations/${sale.id}/parental-auth`,
        form,
        { withCredentials: true }
      );
      if (data.changes > 0) {
        toast.success(`${data.changes} alteração(ões) gravada(s). Registado no histórico.`);
        if (onRefresh) await onRefresh(data.registration);
      } else {
        toast.info("Sem alterações para gravar.");
      }
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gravar alterações.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="minor-parental-dialog">
        <DialogHeader>
          <DialogTitle className="text-cyan-300">
            {isU12 ? "👶" : "🧒"} Termo de Responsabilidade
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Bilhete <span className="font-mono text-amber-300">{sale.ticket_number}</span> · Aceite em {acceptance.accepted_at ? new Date(acceptance.accepted_at).toLocaleString("pt-PT") : "—"}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          {!editing ? (
            <>
              <div className="bg-black/30 border border-cyan-500/20 rounded-lg p-3">
                <p className="text-[10px] uppercase text-cyan-400 tracking-wider mb-1">Menor</p>
                <p className="text-white text-sm font-medium">{minor.full_name || "—"}</p>
                <p className="text-zinc-400 text-[11px] mt-1">
                  Nasc.: {minor.dob || "—"} · {minor.age != null ? `${minor.age} anos` : "—"} · {isU12 ? "≤12" : "13-17"}
                </p>
              </div>
              <div className="bg-black/30 border border-amber-500/20 rounded-lg p-3">
                <p className="text-[10px] uppercase text-amber-400 tracking-wider mb-1">Encarregado de Educação</p>
                <p className="text-white text-sm font-medium">{guardian.full_name || "—"}</p>
                <p className="text-zinc-400 text-[11px] mt-1">{guardian.email || "—"} · {guardian.contact || "—"}{guardian.whatsapp ? ` · WA: ${guardian.whatsapp}` : ""}</p>
                <p className="text-zinc-500 text-[10px] mt-1">CC: {guardian.id_doc || "—"}</p>
                {guardian.address && <p className="text-zinc-500 text-[10px] mt-1">{guardian.address}</p>}
              </div>
              <div className="bg-black/30 border border-emerald-500/20 rounded-lg p-3 md:col-span-2">
                <p className="text-[10px] uppercase text-emerald-400 tracking-wider mb-1">Adulto {roleLbl}</p>
                <p className="text-white text-sm font-medium">{responsible.full_name || "—"}</p>
                <p className="text-zinc-400 text-[11px] mt-1">
                  {responsible.email || "—"} · {responsible.contact || "—"}{responsible.whatsapp ? ` · WA: ${responsible.whatsapp}` : ""}
                </p>
                {responsible.linked_ticket_number && (
                  <p className="text-zinc-500 text-[10px] mt-1">
                    Bilhete do adulto: <span className="font-mono text-amber-300/80">{responsible.linked_ticket_number}</span>
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="md:col-span-2 space-y-4" data-testid="parental-edit-form">
              {/* MINOR */}
              <div className="bg-black/30 border border-cyan-500/20 rounded-lg p-3 space-y-2">
                <p className="text-[10px] uppercase text-cyan-400 tracking-wider">Menor</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-zinc-400 text-xs">Nome completo</Label>
                    <Input value={form.minor.full_name} onChange={(e) => setField("minor", "full_name", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-minor-name" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Data de nascimento</Label>
                    <Input type="date" value={(form.minor.dob || "").slice(0,10)} onChange={(e) => setField("minor", "dob", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-minor-dob" />
                  </div>
                </div>
              </div>
              {/* GUARDIAN */}
              <div className="bg-black/30 border border-amber-500/20 rounded-lg p-3 space-y-2">
                <p className="text-[10px] uppercase text-amber-400 tracking-wider">Encarregado de Educação</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-zinc-400 text-xs">Nome completo</Label>
                    <Input value={form.guardian.full_name} onChange={(e) => setField("guardian", "full_name", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-guardian-name" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">CC / Doc. Identificação</Label>
                    <Input value={form.guardian.id_doc} onChange={(e) => setField("guardian", "id_doc", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-guardian-iddoc" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Email</Label>
                    <Input type="email" value={form.guardian.email} onChange={(e) => setField("guardian", "email", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-guardian-email" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Contacto</Label>
                    <Input value={form.guardian.contact} onChange={(e) => setField("guardian", "contact", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-guardian-contact" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">WhatsApp</Label>
                    <Input value={form.guardian.whatsapp} onChange={(e) => setField("guardian", "whatsapp", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-guardian-whatsapp" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-zinc-400 text-xs">Morada</Label>
                    <Input value={form.guardian.address} onChange={(e) => setField("guardian", "address", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-guardian-address" />
                  </div>
                </div>
              </div>
              {/* RESPONSIBLE */}
              <div className="bg-black/30 border border-emerald-500/20 rounded-lg p-3 space-y-2">
                <p className="text-[10px] uppercase text-emerald-400 tracking-wider">Adulto {roleLbl}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-zinc-400 text-xs">Nome completo</Label>
                    <Input value={form.responsible.full_name} onChange={(e) => setField("responsible", "full_name", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-resp-name" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Email</Label>
                    <Input type="email" value={form.responsible.email} onChange={(e) => setField("responsible", "email", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-resp-email" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Contacto</Label>
                    <Input value={form.responsible.contact} onChange={(e) => setField("responsible", "contact", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-resp-contact" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">WhatsApp</Label>
                    <Input value={form.responsible.whatsapp} onChange={(e) => setField("responsible", "whatsapp", e.target.value)} className="bg-zinc-800 border-white/10 text-white text-sm" data-testid="edit-resp-whatsapp" />
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-amber-300/70">
                A assinatura digital e a aceitação do termo são imutáveis (prova legal) e não podem ser editadas. Todas as alterações ficam registadas no histórico.
              </p>
            </div>
          )}
        </div>

        {auth.signature?.data_url && !editing && (
          <div className="mt-3 bg-black/30 border border-white/5 rounded-lg p-3">
            <p className="text-[10px] uppercase text-zinc-500 tracking-wider mb-1">Assinatura digital</p>
            <img src={auth.signature.data_url} alt="Assinatura" className="max-h-24 bg-white rounded-md" />
            <p className="text-[10px] text-zinc-600 mt-1 font-mono">
              Nº autorização: {auth.auth_id} · IP: {acceptance.ip || "—"} · Termo v{acceptance.terms_version || "?"} · SHA256: {(acceptance.terms_hash || "").slice(0, 16)}…
            </p>
          </div>
        )}

        {/* AUDIT LOG */}
        {auditLog.length > 0 && !editing && (
          <div className="mt-3 bg-black/30 border border-violet-500/20 rounded-lg p-3" data-testid="parental-audit-log">
            <button
              type="button"
              onClick={() => setShowAudit((v) => !v)}
              className="w-full flex items-center justify-between text-left"
              data-testid="parental-audit-toggle"
            >
              <span className="text-[10px] uppercase text-violet-300 tracking-wider">
                Histórico de alterações ({auditLog.length})
              </span>
              <ChevronDown className={`w-4 h-4 text-violet-300 transition-transform ${showAudit ? "rotate-180" : ""}`} />
            </button>
            {showAudit && (
              <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {auditLog.slice().reverse().map((e, i) => (
                  <li key={i} className="text-[11px] text-zinc-300 border-l-2 border-violet-500/40 pl-2">
                    <span className="text-violet-300/80 font-mono">[{e.at ? new Date(e.at).toLocaleString("pt-PT") : "—"}]</span>{" "}
                    <span className="text-zinc-400">{e.by_admin || "admin"}</span> alterou{" "}
                    <span className="text-amber-300">{e.block}.{e.field}</span>:{" "}
                    <span className="text-zinc-500 line-through">{e.old || "—"}</span> →{" "}
                    <span className="text-emerald-300">{e.new || "—"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          {sale.parental_email_sent_at && !editing && (
            <span className="text-emerald-300 text-[11px] self-center mr-auto">
              ✓ Email enviado em {new Date(sale.parental_email_sent_at).toLocaleString("pt-PT")}
            </span>
          )}
          {editing ? (
            <>
              <Button onClick={() => setEditing(false)} variant="outline" className="border-white/20 text-zinc-300 hover:bg-white/5" data-testid="parental-edit-cancel">
                Cancelar
              </Button>
              <Button onClick={saveEdit} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="parental-edit-save">
                {saving ? "A gravar..." : "Gravar alterações"}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setEditing(true)} variant="outline" className="border-violet-500/30 text-violet-300 hover:bg-violet-500/10" data-testid="parental-edit-toggle">
                <Edit className="w-4 h-4 mr-2" /> Editar
              </Button>
              <Button onClick={resend} disabled={resending} variant="outline" className="border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10" data-testid="minor-resend-email">
                {resending ? "A enviar..." : "Reenviar email"}
              </Button>
              <Button onClick={downloadPdf} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="minor-download-pdf">
                <FileText className="w-4 h-4 mr-2" /> Descarregar PDF
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ────────────────────────────────────────────────────────────────────
// Plan history dialog — shows each installment payment (date, time,
// method, amount, note) for a registration in installments mode.
// ────────────────────────────────────────────────────────────────────
const PlanHistoryDialog = ({ sale, onClose, accentColor }) => {
  const open = !!sale;
  if (!sale) return null;
  const installments = (sale.installments || []).slice().sort((a, b) =>
    new Date(b.paid_at).getTime() - new Date(a.paid_at).getTime()
  );
  const totalDue = Number(sale.total_due || sale.final_price || 0);
  const totalPaid = Number(sale.total_paid || 0);
  const remaining = Math.max(0, totalDue - totalPaid);
  const percent = totalDue > 0 ? Math.min(100, Math.round((totalPaid / totalDue) * 100)) : 0;
  const status = sale.plan_status || "active";
  const statusLbl = ({
    active: "Em curso", completed: "Concluído", cancelled: "Cancelado",
    overdue: "Em atraso", frozen: "Congelado", expired: "Expirado",
  })[status] || status;

  const fmt = (iso) => {
    if (!iso) return { date: "—", time: "" };
    try {
      const d = new Date(iso);
      return {
        date: d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" }),
        time: d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" }),
      };
    } catch { return { date: iso, time: "" }; }
  };

  const methodMeta = (m) => {
    const k = (m || "").toLowerCase();
    if (k === "stripe") return { lbl: "Stripe (cartão/MB Way)", cls: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" };
    if (k === "offline" || k === "transferencia" || k === "transferência" || k === "transfer") return { lbl: "Transferência", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" };
    if (k === "cash" || k === "dinheiro") return { lbl: "Dinheiro", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" };
    if (k === "mbway") return { lbl: "MB Way", cls: "bg-pink-500/15 text-pink-300 border-pink-500/30" };
    if (k === "manual") return { lbl: "Manual", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" };
    return { lbl: m || "—", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" };
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="plan-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-400">Histórico de prestações</DialogTitle>
          <DialogDescription className="text-zinc-500">
            {sale.first_name} {sale.last_name} · <span className="font-mono text-amber-300">{sale.ticket_number}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-2 mt-2">
          <div className="bg-black/30 border border-white/5 rounded-lg p-3">
            <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Total</p>
            <p className="text-lg font-bold text-white">{totalDue.toFixed(2)}€</p>
          </div>
          <div className="bg-black/30 border border-emerald-500/20 rounded-lg p-3">
            <p className="text-[10px] uppercase text-emerald-400 tracking-wider">Pago</p>
            <p className="text-lg font-bold text-emerald-300">{totalPaid.toFixed(2)}€</p>
          </div>
          <div className="bg-black/30 border border-amber-500/20 rounded-lg p-3">
            <p className="text-[10px] uppercase text-amber-400 tracking-wider">Restante</p>
            <p className="text-lg font-bold text-amber-300">{remaining.toFixed(2)}€</p>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-zinc-400 mt-3">
          <span>Estado: <strong className="text-white">{statusLbl}</strong></span>
          <span>{percent}% pago</span>
        </div>
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mt-1.5">
          <div className="h-full rounded-full transition-all" style={{ width: `${percent}%`, background: accentColor }} />
        </div>

        {/* Installments list */}
        <div className="mt-4">
          <p className="text-amber-400 text-[10px] uppercase tracking-[2px] font-semibold mb-2">
            Prestações ({installments.length})
          </p>
          {installments.length === 0 ? (
            <div className="bg-black/30 border border-white/5 rounded-lg p-6 text-center">
              <p className="text-zinc-400 text-sm">Sem prestações registadas ainda.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {installments.map((inst, idx) => {
                const { date, time } = fmt(inst.paid_at);
                const mm = methodMeta(inst.payment_method);
                return (
                  <div key={inst.id || idx} className="bg-black/30 border border-white/5 rounded-lg p-3 flex items-start gap-3" data-testid={`plan-history-row-${idx}`}>
                    <div className="flex-shrink-0 w-10 text-center">
                      <p className="text-zinc-600 text-[10px] uppercase">#</p>
                      <p className="text-amber-300 font-bold text-sm">{installments.length - idx}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-emerald-300 font-bold font-mono text-sm">+{Number(inst.amount || 0).toFixed(2)}€</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${mm.cls}`}>{mm.lbl}</span>
                      </div>
                      <p className="text-xs text-zinc-400 mt-1">
                        <span className="text-zinc-300">{date}</span>
                        {time && <span className="text-zinc-500"> · {time}</span>}
                      </p>
                      {inst.note && (
                        <p className="text-[11px] text-zinc-500 mt-1 italic">{inst.note}</p>
                      )}
                      {inst.stripe_session_id && (
                        <p className="text-[10px] text-zinc-600 font-mono mt-0.5 truncate">Stripe ref: {inst.stripe_session_id}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end">
          <a
            href={`/admin/plans`}
            className="text-xs text-amber-400 hover:text-amber-300"
            data-testid="plan-history-goto-plans"
          >
            Gerir este plano em /admin/plans →
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};



export const SalesManagement = () => {
  const [sales, setSales] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [allTicketTypes, setAllTicketTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get("status") || "all");
  const [filterTicketType, setFilterTicketType] = useState(() => searchParams.get("ticket_type") || "all");
  const [filterRoom, setFilterRoom] = useState(() => {
    const raw = searchParams.get("room") || "";
    if (!raw || raw === "all") return new Set();
    return new Set(raw.split(",").filter(Boolean));
  });
  const [filterSession, setFilterSession] = useState(() => searchParams.get("session") || "all");
  const [filterPlan, setFilterPlan] = useState(() => searchParams.get("plan") || "all");
  const [filterMinor, setFilterMinor] = useState(() => searchParams.get("minor") || "all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [planHistorySale, setPlanHistorySale] = useState(null);
  const [minorSale, setMinorSale] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  const [showPdfPicker, setShowPdfPicker] = useState(false);
  const [showData, setShowData] = useState(null);
  const [newSale, setNewSale] = useState({
    session_id: "",
    ticket_type_id: "",
    coupon_code: "",
    name: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    church: "",
    age: "",
  });
  const [newSaleTicketTypes, setNewSaleTicketTypes] = useState([]);
  const [couponInfo, setCouponInfo] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  // Reserved core names — see /app/frontend/src/lib/reservedFields.js for the shared list.
  const nonCoreCustomFields = customFields.filter((cf) => !RESERVED_CORE_FIELD_NAMES.includes(cf.name));
  // Track which default-field dropdowns are in "Outro" (free-text) mode.
  const [newSaleOther, setNewSaleOther] = useState({});
  const [showColsPicker, setShowColsPicker] = useState(false);
  // Default: all "core" columns visible (matches legacy table), custom fields off by default.
  const SALES_DEFAULT_COLS = [
    "actions","status","created_at","ticket_number","first_name","last_name","email","phone",
    "church","age","group_code","vip","session_date","ticket_type_name","plan","minor",
    "final_price","amount_paid","amount_due","payment_method","coupon_code","checked_in",
  ];
  const SALES_COLS_STORAGE = "sales-table-cols-v2";
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem(SALES_COLS_STORAGE);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return new Set(parsed);
      }
    } catch {}
    return new Set(SALES_DEFAULT_COLS);
  });
  const colVisible = (k) => visibleCols.has(k);

  useEffect(() => {
    try { localStorage.setItem(SALES_COLS_STORAGE, JSON.stringify(Array.from(visibleCols))); } catch {}
  }, [visibleCols]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [salesRes, sessionsRes, showRes, cfRes, ttRes, roomsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/sales`, { withCredentials: true }),
        axios.get(`${API_URL}/api/sessions`),
        axios.get(`${API_URL}/api/admin/show`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/custom-fields`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/ticket-types`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/rooms`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSales(salesRes.data);
      setSessions(sessionsRes.data);
      setShowData(showRes.data);
      setCustomFields(Array.isArray(cfRes.data) ? cfRes.data : []);
      setAllTicketTypes(Array.isArray(ttRes.data) ? ttRes.data : []);
      setRooms(Array.isArray(roomsRes.data) ? roomsRes.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSale = async () => {
    if (!newSale.session_id || !newSale.first_name || !newSale.email) {
      toast.error("Preencha sessão, primeiro nome e email");
      return;
    }
    try {
      const payload = {
        ...newSale,
        age: newSale.age ? parseInt(newSale.age) : undefined,
        coupon_code: newSale.coupon_code || undefined,
        ticket_type_id: newSale.ticket_type_id || undefined,
      };
      await axios.post(
        `${API_URL}/api/admin/sales`,
        payload,
        { withCredentials: true }
      );
      toast.success("Bilhete criado com sucesso!");
      setIsCreateOpen(false);
      setNewSale({ session_id: "", ticket_type_id: "", coupon_code: "", name: "", first_name: "", last_name: "", email: "", phone: "", church: "", age: "" });
      setNewSaleTicketTypes([]);
      setCouponInfo(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar bilhete");
    }
  };

  const handleNewSaleSessionChange = async (sessionId) => {
    setNewSale({ ...newSale, session_id: sessionId, ticket_type_id: "" });
    try {
      const res = await axios.get(`${API_URL}/api/ticket-types/${sessionId}`);
      setNewSaleTicketTypes(res.data);
    } catch {
      setNewSaleTicketTypes([]);
    }
  };

  const handleValidateCoupon = async () => {
    if (!newSale.coupon_code) return;
    try {
      const res = await axios.post(`${API_URL}/api/validate-coupon`, {
        code: newSale.coupon_code,
        ticket_type_id: newSale.ticket_type_id || undefined,
      });
      setCouponInfo(res.data);
      toast.success(`Cupão válido: ${res.data.discount_type === "percentage" ? res.data.discount_value + "%" : res.data.discount_value + "€"} de desconto`);
    } catch (err) {
      setCouponInfo(null);
      toast.error(err.response?.data?.detail || "Cupão inválido");
    }
  };

  const handleDeleteSale = async (saleId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/sales/${saleId}`, { withCredentials: true });
      toast.success("Venda eliminada com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao eliminar venda");
    }
  };

  const handleDownloadTicketPDF = async (sale) => {
    try {
      const showRes = await axios.get(`${API_URL}/api/admin/show`, { withCredentials: true });
      const show = showRes.data;
      const ac = show?.accent_color || "#f59e0b";
      const badge = show?.hero_badge || "";

      const QRCode = (await import("qrcode")).default;
      const qrData = `TICKET:${sale.ticket_number}|NAME:${sale.name}|SESSION:${sale.session_date} ${sale.session_time}`;
      const qrDataUrl = await QRCode.toDataURL(qrData, { width: 200, margin: 2 });

      const pdf = new jsPDF("p", "mm", "a4");
      const w = pdf.internal.pageSize.getWidth();
      const cx = w / 2;

      // Background
      pdf.setFillColor(24, 24, 27);
      pdf.rect(0, 0, w, 297, "F");

      // Card
      const cardX = 30, cardY = 20, cardW = w - 60;
      pdf.setFillColor(39, 39, 42);
      pdf.roundedRect(cardX, cardY, cardW, 230, 4, 4, "F");

      // Header
      const r = parseInt(ac.slice(1, 3), 16), g = parseInt(ac.slice(3, 5), 16), b = parseInt(ac.slice(5, 7), 16);
      pdf.setTextColor(r, g, b);
      pdf.setFontSize(22);
      pdf.setFont("helvetica", "bold");
      pdf.text(show.name || "", cx, 40, { align: "center" });

      if (badge) {
        pdf.setFontSize(9);
        pdf.setTextColor(161, 161, 170);
        pdf.text(badge.toUpperCase(), cx, 48, { align: "center" });
      }

      pdf.setFontSize(10);
      pdf.setTextColor(161, 161, 170);
      pdf.text(sale.ticket_number, cx, 56, { align: "center" });

      // Dashed line
      pdf.setDrawColor(r, g, b);
      pdf.setLineDashPattern([2, 2], 0);
      pdf.line(cardX + 8, 62, cardX + cardW - 8, 62);

      // Fields
      const fields = [
        ["Nome", sale.name],
        ["Data", sale.session_date],
        ["Horário", sale.session_time],
        ["Local", show.location || ""],
      ];
      if (sale.church) fields.push(["Igreja", sale.church]);

      let y = 74;
      pdf.setLineDashPattern([], 0);
      fields.forEach(([label, value]) => {
        pdf.setFontSize(10);
        pdf.setTextColor(161, 161, 170);
        pdf.text(label, cardX + 16, y);
        pdf.setTextColor(250, 250, 250);
        pdf.setFont("helvetica", "bold");
        pdf.text(value || "", cardX + cardW - 16, y, { align: "right" });
        pdf.setFont("helvetica", "normal");
        pdf.setDrawColor(255, 255, 255, 15);
        pdf.setLineDashPattern([], 0);
        pdf.line(cardX + 12, y + 4, cardX + cardW - 12, y + 4);
        y += 14;
      });

      // Dashed line before QR
      pdf.setDrawColor(r, g, b);
      pdf.setLineDashPattern([2, 2], 0);
      pdf.line(cardX + 8, y + 4, cardX + cardW - 8, y + 4);

      // QR label
      pdf.setFontSize(10);
      pdf.setTextColor(161, 161, 170);
      pdf.setLineDashPattern([], 0);
      pdf.text("Apresente este QR Code na entrada", cx, y + 16, { align: "center" });

      // QR code
      const qrSize = 40;
      pdf.addImage(qrDataUrl, "PNG", cx - qrSize / 2, y + 20, qrSize, qrSize);

      // Footer
      pdf.setFontSize(9);
      pdf.setTextColor(161, 161, 170);
      pdf.text("Este bilhete é pessoal e intransmissível", cx, y + 66, { align: "center" });
      pdf.setTextColor(r, g, b);
      pdf.text("\u00A9 BILHETEZ", cx, y + 72, { align: "center" });

      pdf.save(`bilhete-${sale.ticket_number}.pdf`);
      toast.success("PDF do bilhete gerado!");
    } catch (error) {
      console.error("PDF error:", error);
      toast.error("Erro ao gerar PDF");
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  // Map session_id → room_id so we can filter registrations by room.
  const sessionRoomMap = useMemo(() => {
    const m = new Map();
    sessions.forEach((s) => m.set(s.id, s.room_id || null));
    return m;
  }, [sessions]);

  const filteredAndSortedSales = useMemo(() => {
    let filtered = sales.filter((sale) => {
      const matchesSearch =
        sale.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sale.first_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sale.last_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sale.phone || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.ticket_number.toLowerCase().includes(searchTerm.toLowerCase());
      // Filter "pending" bundles both pure `pending` and `installments` — same
      // rule the Dashboard uses. See lib/paymentStatus.js.
      const matchesFilter = filterStatus === "all"
        || (filterStatus === "pending"
              ? isPendingStatus(sale.payment_status)
              : sale.payment_status === filterStatus);
      // filterTicketType stores the value as "id:<uuid>" for types linked
      // to a ticket_type_id, or "name:<name>" for legacy rows without id.
      // This makes filtering robust to type renames.
      let matchesTicketType = filterTicketType === "all";
      if (!matchesTicketType) {
        if (filterTicketType.startsWith("id:")) {
          matchesTicketType = sale.ticket_type_id === filterTicketType.slice(3);
        } else if (filterTicketType.startsWith("name:")) {
          matchesTicketType = (sale.ticket_type_name || "(sem tipo)") === filterTicketType.slice(5);
        } else {
          // Legacy value (raw name) — keep backwards compatibility for
          // URLs bookmarked with ?ticket_type=<name>.
          matchesTicketType = (sale.ticket_type_name || "(sem tipo)") === filterTicketType;
        }
      }
      const matchesSession = filterSession === "all" || sale.session_id === filterSession;
      const saleRoomId = sessionRoomMap.get(sale.session_id) || null;
      const matchesRoom = filterRoom.size === 0
        || filterRoom.has(saleRoomId || "__none__");
      const matchesPlan =
        filterPlan === "all" ||
        (filterPlan === "plan_yes" && sale.is_installment_plan) ||
        (filterPlan === "plan_no" && !sale.is_installment_plan) ||
        (filterPlan === sale.plan_status);
      const matchesMinor =
        filterMinor === "all" ||
        (filterMinor === "minor_yes" && !!sale.parental_auth) ||
        (filterMinor === "minor_no" && !sale.parental_auth) ||
        (filterMinor === "child_under_12" && (sale.parental_auth?.minor?.category === "child_under_12")) ||
        (filterMinor === "teen_13_17" && (sale.parental_auth?.minor?.category === "teen_13_17"));
      return matchesSearch && matchesFilter && matchesTicketType && matchesRoom && matchesSession && matchesPlan && matchesMinor;
    });

    return filtered.sort((a, b) => {
      let aVal = a[sortConfig.key] || "";
      let bVal = b[sortConfig.key] || "";
      // Date fields: compare as ISO strings (works lexicographically)
      if (sortConfig.key === "created_at" || sortConfig.key === "checked_in_at") {
        aVal = a[sortConfig.key] || "";
        bVal = b[sortConfig.key] || "";
      } else {
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [sales, searchTerm, filterStatus, filterTicketType, filterRoom, filterSession, filterPlan, filterMinor, sortConfig, sessionRoomMap]);

  // Distinct ticket type names from the loaded sales (memo to avoid re-eval on every render)
  // Session options preserve the order defined in the Configurations page
  // (order field ascending); sessions with zero sales are still shown.
  const sessionOptions = useMemo(() => {
    const counts = new Map();
    sales.forEach((s) => counts.set(s.session_id, (counts.get(s.session_id) || 0) + 1));
    return sessions
      .slice()
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
      .map((s) => ({
        id: s.id,
        label: [s.name, s.subtitle, s.date, s.time].filter(Boolean).join(" · ") || "(sessão sem nome)",
        count: counts.get(s.id) || 0,
      }));
  }, [sales, sessions]);

  // Room options ordered by the Configurations page (order asc). Includes a
  // trailing "(sem sala)" bucket when there are sessions without a room.
  const roomOptions = useMemo(() => {
    const counts = new Map();
    let noneCount = 0;
    sales.forEach((s) => {
      const roomId = sessionRoomMap.get(s.session_id) || null;
      if (!roomId) { noneCount += 1; return; }
      counts.set(roomId, (counts.get(roomId) || 0) + 1);
    });
    const list = rooms
      .slice()
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
      .map((r) => ({ id: r.id, name: r.name || "(sala sem nome)", count: counts.get(r.id) || 0 }));
    if (noneCount > 0) list.push({ id: "__none__", name: "(sem sala)", count: noneCount });
    return list;
  }, [sales, rooms, sessionRoomMap]);

  const distinctTicketTypes = useMemo(() => {
    // Build a map keyed by ticket_type_id when available (falls back to the
    // cached name for legacy rows). Then union with the *current* list of
    // ticket types so the filter also lists types that have zero sales
    // yet, and always shows the up-to-date name (not the stale one that
    // was cached on the registration when the type had a different name).
    const byId = new Map();
    const byName = new Map();
    const idToCurrent = new Map(); // id -> current name
    const idToOrder = new Map();   // id -> configured order
    allTicketTypes.forEach((t) => {
      if (t?.id) {
        idToCurrent.set(t.id, t.name || "(sem nome)");
        idToOrder.set(t.id, t.order ?? Number.POSITIVE_INFINITY);
      }
      byId.set(t.id || null, { id: t.id || null, name: t.name || "(sem tipo)", count: 0 });
    });
    sales.forEach((s) => {
      const id = s.ticket_type_id || null;
      const cachedName = s.ticket_type_name || "(sem tipo)";
      const currentName = id && idToCurrent.has(id) ? idToCurrent.get(id) : cachedName;
      if (id) {
        const prev = byId.get(id) || { id, name: currentName, count: 0 };
        prev.name = currentName; // keep current name
        prev.count += 1;
        byId.set(id, prev);
      } else {
        const prev = byName.get(cachedName) || { id: null, name: cachedName, count: 0 };
        prev.count += 1;
        byName.set(cachedName, prev);
      }
    });
    return [...byId.values(), ...byName.values()]
      .sort((a, b) => {
        // Preserve the order defined in the Configurations page. Items
        // linked to a ticket_type_id inherit that type's `order`; legacy
        // rows without an id are pushed to the end.
        const ao = a.id && idToOrder.has(a.id) ? idToOrder.get(a.id) : Number.POSITIVE_INFINITY;
        const bo = b.id && idToOrder.has(b.id) ? idToOrder.get(b.id) : Number.POSITIVE_INFINITY;
        if (ao !== bo) return ao - bo;
        return a.name.localeCompare(b.name);
      });
  }, [sales, allTicketTypes]);

  const exportData = filteredAndSortedSales.map(s => {
    const paid = s.amount_paid != null ? Number(s.amount_paid) : (s.payment_status === "paid" ? (s.final_price || 0) : 0);
    const due = Math.max(0, (s.final_price || 0) - paid);
    // Plan-related summary
    let plano = "—";
    if (s.is_installment_plan) {
      const planDue = Number(s.total_due || s.final_price || 0);
      const planPaid = Number(s.total_paid || 0);
      const pct = planDue > 0 ? Math.round((planPaid / planDue) * 100) : 0;
      const statusLbl = ({ active: "Em curso", completed: "Concluído", cancelled: "Cancelado",
                           overdue: "Em atraso", frozen: "Congelado", expired: "Expirado" })[s.plan_status || "active"] || (s.plan_status || "—");
      plano = `${statusLbl} (${pct}% — ${planPaid.toFixed(2)}€/${planDue.toFixed(2)}€)`;
    }
    return {
      bilhete: s.ticket_number,
      data_compra: s.created_at ? new Date(s.created_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-",
      primeiro_nome: s.first_name || "",
      ultimo_nome: s.last_name || "",
      email: s.email,
      telefone: s.phone,
      igreja: s.church,
      idade: s.age,
      grupo: s.group_code || "-",
      vip: s.is_vip ? "Sim" : "Não",
      sessao: `${s.session_date} ${s.session_time}`,
      tipo_bilhete: s.ticket_type_name || "-",
      plano,
      menor: s.parental_auth ? `${(s.parental_auth.minor?.category === "child_under_12" ? "≤12" : "13-17")} · ${s.parental_auth.minor?.full_name || ""} (${s.parental_auth.minor?.age ?? "?"}a) · ${s.parental_auth.minor?.category === "child_under_12" ? "Acomp" : "Resp"}: ${s.parental_auth.responsible?.full_name || ""}` : "—",
      valor_a_cobrar: s.final_price != null ? s.final_price.toFixed(2) : "-",
      valor_pago: paid.toFixed(2),
      valor_em_falta: due.toFixed(2),
      tipo_pagamento: s.payment_method || "-",
      cupao: s.coupon_code || "-",
      checkin: s.checked_in ? "Sim" : "Não",
      status: s.payment_status === "paid" ? "Pago" : "Pendente",
      // Custom fields appended dynamically — key is `cf_<name>` so exports/PDF picker can address them.
      // Skip fields whose name overlaps a "core" Registration column (first_name, last_name, email,
      // phone, church, age): those already have dedicated top-level columns above; duplicating them
      // as cf_* would create the exact confusion the admin was complaining about.
      ...Object.fromEntries(nonCoreCustomFields.map((cf) => {
        const v = s.custom_fields?.[cf.name];
        let val = "—";
        if (v !== undefined && v !== null && v !== "") {
          val = cf.field_type === "checkbox" ? (v ? "Sim" : "Não") : String(v);
        }
        return [`cf_${cf.name}`, val];
      })),
    };
  });

  const columns = [
    { key: "bilhete", header: "Bilhete" },
    { key: "data_compra", header: "Data Compra" },
    { key: "primeiro_nome", header: "Primeiro Nome" },
    { key: "ultimo_nome", header: "Último Nome" },
    { key: "email", header: "Email" },
    { key: "telefone", header: "Telefone" },
    { key: "igreja", header: "Igreja" },
    { key: "idade", header: "Idade" },
    { key: "grupo", header: "Grupo" },
    { key: "vip", header: "VIP" },
    { key: "sessao", header: "Sessão" },
    { key: "tipo_bilhete", header: "Tipo de Bilhete" },
    { key: "plano", header: "Plano" },
    { key: "menor", header: "Menor" },
    { key: "valor_a_cobrar", header: "Valor a Cobrar" },
    { key: "valor_pago", header: "Valor Pago" },
    { key: "valor_em_falta", header: "Valor em Falta" },
    { key: "tipo_pagamento", header: "Tipo Pagamento" },
    { key: "cupao", header: "Cupão" },
    { key: "checkin", header: "Check-in" },
    { key: "status", header: "Status" },
    ...nonCoreCustomFields.map((cf) => ({ key: `cf_${cf.name}`, header: cf.label })),
  ];

  if (loading) {
    return <div className="animate-pulse">A carregar...</div>;
  }

  return (
    <div data-testid="sales-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestão de Bilhetes</h1>
          <p className="text-zinc-500 text-sm mt-1">Gerir a Tabela de Bilhetes</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={async () => {
              if (!window.confirm("Vai eliminar bilhetes pendentes que já têm uma compra paga gémea (mesmo email/sessão/tipo). Continuar?")) return;
              try {
                const r = await axios.post(`${API_URL}/api/admin/cleanup-orphan-pendings`, {}, { withCredentials: true });
                toast.success(r.data.message || "Limpeza concluída");
                fetchData();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Erro na limpeza");
              }
            }}
            className="btn-secondary"
            data-testid="btn-cleanup-orphans"
            title="Apaga pendentes que já têm um bilhete pago do mesmo cliente para a mesma sessão/tipo"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Limpar Órfãos
          </Button>
          <Button
            onClick={async () => {
              try {
                const r = await axios.post(`${API_URL}/api/admin/reconcile-stripe`, {}, { withCredentials: true });
                toast.success(r.data.message || "Reconciliação concluída");
                fetchData();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Erro na reconciliação");
              }
            }}
            className="btn-secondary"
            data-testid="btn-reconcile-stripe"
            title="Confirma vendas pendentes que ficaram presas (ex: MB WAY confirmou tarde)"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reconciliar Stripe
          </Button>
          <Button onClick={() => setShowColsPicker(true)} className="btn-secondary" data-testid="open-cols-picker" title="Escolher colunas visíveis na tabela">
            <Eye className="w-4 h-4 mr-2" />
            Colunas
          </Button>
          <Button onClick={() => setShowPdfPicker(true)} className="btn-secondary" data-testid="open-pdf-picker">
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button onClick={() => exportToExcel(exportData, "vendas")} className="btn-secondary">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-sale-btn">
                <Plus className="w-4 h-4 mr-2" />
                Novo Bilhete
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white">Novo Bilhete</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-1">
                <div>
                  <Label className="text-zinc-300">Sessão</Label>
                  <Select
                    value={newSale.session_id}
                    onValueChange={handleNewSaleSessionChange}
                  >
                    <SelectTrigger className="input-dark mt-2">
                      <SelectValue placeholder="Selecione uma sessão" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {sessions.map((session) => {
                        const parts = [];
                        if (session.name) parts.push(session.name);
                        if (session.subtitle) parts.push(session.subtitle);
                        if (session.date) parts.push(session.date);
                        if (session.time) parts.push(session.time);
                        const label = parts.join(" · ") || "(sessão sem nome)";
                        return (
                          <SelectItem key={session.id} value={session.id} className="text-white">
                            {label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                {newSale.session_id && newSaleTicketTypes.length > 0 && (
                  <div>
                    <Label className="text-zinc-300">Tipo de Bilhete</Label>
                    <Select
                      value={newSale.ticket_type_id}
                      onValueChange={(value) => setNewSale({ ...newSale, ticket_type_id: value })}
                    >
                      <SelectTrigger className="input-dark mt-2" data-testid="new-sale-ticket-type">
                        <SelectValue placeholder="Selecione tipo de bilhete" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        {newSaleTicketTypes.map((tt) => (
                          <SelectItem key={tt.id} value={tt.id} className="text-white">
                            {tt.name} - {tt.price.toFixed(2)}€
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-zinc-300">Cupão de Desconto</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newSale.coupon_code}
                      onChange={(e) => { setNewSale({ ...newSale, coupon_code: e.target.value.toUpperCase() }); setCouponInfo(null); }}
                      className="input-dark flex-1 font-mono"
                      placeholder="Ex: GRATIS100"
                      data-testid="new-sale-coupon"
                    />
                    <Button onClick={handleValidateCoupon} variant="outline" className="btn-secondary text-sm" disabled={!newSale.coupon_code}>
                      Validar
                    </Button>
                  </div>
                  {couponInfo && (
                    <p className="text-green-400 text-xs mt-1">
                      Desconto: {couponInfo.discount_type === "percentage" ? `${couponInfo.discount_value}%` : `${couponInfo.discount_value}€`}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-zinc-300">Nome Completo</Label>
                  <Input
                    value={newSale.name}
                    onChange={(e) => {
                      const full = e.target.value;
                      const parts = full.trim().split(/\s+/);
                      const first_name = parts.shift() || "";
                      const last_name = parts.join(" ");
                      setNewSale({ ...newSale, name: full, first_name, last_name });
                    }}
                    className="input-dark mt-2"
                    placeholder="Primeiro Último"
                    data-testid="new-sale-name"
                  />
                  <p className="text-zinc-500 text-xs mt-1">A primeira palavra é o primeiro nome; o resto vai para apelido.</p>
                </div>
                <div>
                  <Label className="text-zinc-300">Email</Label>
                  <Input
                    type="email"
                    value={newSale.email}
                    onChange={(e) => setNewSale({ ...newSale, email: e.target.value })}
                    className="input-dark mt-2"
                    data-testid="new-sale-email"
                  />
                  {(() => {
                    const s = suggestEmailFix(newSale.email);
                    if (!s) return null;
                    return (
                      <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100"
                           data-testid="new-sale-email-suggestion">
                        <span>Quis dizer <strong className="font-semibold text-amber-50">{s.suggestion}</strong>?</span>
                        <button
                          type="button"
                          onClick={() => setNewSale({ ...newSale, email: s.suggestion })}
                          className="rounded-md bg-amber-500 px-2.5 py-1 text-[11px] font-semibold text-black hover:bg-amber-400 transition-colors"
                          data-testid="new-sale-email-apply"
                        >Aplicar</button>
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <Label className="text-zinc-300">Telefone</Label>
                  {(() => {
                    const cf = customFields.find((c) => c.name === "phone");
                    const isOther = !!newSaleOther.phone;
                    if (cf && cf.field_type === "dropdown" && Array.isArray(cf.options) && cf.options.length > 0) {
                      return (
                        <>
                          <Select
                            value={isOther ? "__OTHER__" : (newSale.phone || "")}
                            onValueChange={(v) => {
                              if (v === "__OTHER__") {
                                setNewSaleOther((p) => ({ ...p, phone: true }));
                                setNewSale({ ...newSale, phone: "" });
                              } else {
                                setNewSaleOther((p) => ({ ...p, phone: false }));
                                setNewSale({ ...newSale, phone: v });
                              }
                            }}
                          >
                            <SelectTrigger className="input-dark mt-2" data-testid="new-sale-phone-select">
                              <SelectValue placeholder={cf.placeholder || "Selecione…"} />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-white/10">
                              {cf.options.map((opt) => (
                                <SelectItem key={opt} value={opt} className="text-white">{opt}</SelectItem>
                              ))}
                              {cf.allow_other && (
                                <SelectItem value="__OTHER__" className="text-amber-300">Outro</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          {isOther && (
                            <Input
                              value={newSale.phone}
                              onChange={(e) => setNewSale({ ...newSale, phone: e.target.value })}
                              placeholder="Especifique"
                              className="input-dark mt-2"
                              data-testid="new-sale-phone-other"
                            />
                          )}
                        </>
                      );
                    }
                    return (
                      <Input
                        value={newSale.phone}
                        onChange={(e) => setNewSale({ ...newSale, phone: e.target.value })}
                        className="input-dark mt-2"
                        data-testid="new-sale-phone"
                      />
                    );
                  })()}
                </div>
                <div>
                  <Label className="text-zinc-300">Igreja</Label>
                  {(() => {
                    const cf = customFields.find((c) => c.name === "church");
                    const isOther = !!newSaleOther.church;
                    if (cf && cf.field_type === "dropdown" && Array.isArray(cf.options) && cf.options.length > 0) {
                      return (
                        <>
                          <Select
                            value={isOther ? "__OTHER__" : (newSale.church || "")}
                            onValueChange={(v) => {
                              if (v === "__OTHER__") {
                                setNewSaleOther((p) => ({ ...p, church: true }));
                                setNewSale({ ...newSale, church: "" });
                              } else {
                                setNewSaleOther((p) => ({ ...p, church: false }));
                                setNewSale({ ...newSale, church: v });
                              }
                            }}
                          >
                            <SelectTrigger className="input-dark mt-2" data-testid="new-sale-church-select">
                              <SelectValue placeholder={cf.placeholder || "Selecione…"} />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-white/10">
                              {cf.options.map((opt) => (
                                <SelectItem key={opt} value={opt} className="text-white">{opt}</SelectItem>
                              ))}
                              {cf.allow_other && (
                                <SelectItem value="__OTHER__" className="text-amber-300">Outro</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          {isOther && (
                            <Input
                              value={newSale.church}
                              onChange={(e) => setNewSale({ ...newSale, church: e.target.value })}
                              placeholder="Especifique a igreja"
                              className="input-dark mt-2"
                              data-testid="new-sale-church-other"
                            />
                          )}
                        </>
                      );
                    }
                    return (
                      <Input
                        value={newSale.church}
                        onChange={(e) => setNewSale({ ...newSale, church: e.target.value })}
                        className="input-dark mt-2"
                        data-testid="new-sale-church"
                      />
                    );
                  })()}
                </div>
                <div>
                  <Label className="text-zinc-300">Idade</Label>
                  {(() => {
                    const cf = customFields.find((c) => c.name === "age");
                    const isOther = !!newSaleOther.age;
                    if (cf && cf.field_type === "dropdown" && Array.isArray(cf.options) && cf.options.length > 0) {
                      return (
                        <>
                          <Select
                            value={isOther ? "__OTHER__" : (newSale.age || "")}
                            onValueChange={(v) => {
                              if (v === "__OTHER__") {
                                setNewSaleOther((p) => ({ ...p, age: true }));
                                setNewSale({ ...newSale, age: "" });
                              } else {
                                setNewSaleOther((p) => ({ ...p, age: false }));
                                setNewSale({ ...newSale, age: v });
                              }
                            }}
                          >
                            <SelectTrigger className="input-dark mt-2" data-testid="new-sale-age-select">
                              <SelectValue placeholder={cf.placeholder || "Selecione…"} />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-white/10">
                              {cf.options.map((opt) => (
                                <SelectItem key={opt} value={opt} className="text-white">{opt}</SelectItem>
                              ))}
                              {cf.allow_other && (
                                <SelectItem value="__OTHER__" className="text-amber-300">Outro</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          {isOther && (
                            <Input
                              value={newSale.age}
                              onChange={(e) => setNewSale({ ...newSale, age: e.target.value })}
                              placeholder="Especifique"
                              className="input-dark mt-2"
                              data-testid="new-sale-age-other"
                            />
                          )}
                        </>
                      );
                    }
                    return (
                      <Input
                        type="number"
                        value={newSale.age}
                        onChange={(e) => setNewSale({ ...newSale, age: e.target.value })}
                        className="input-dark mt-2"
                        data-testid="new-sale-age"
                      />
                    );
                  })()}
                </div>
                <Button onClick={handleCreateSale} className="w-full btn-primary" data-testid="confirm-create-sale">
                  Criar Bilhete
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            placeholder="Pesquisar por nome, email, telefone ou bilhete..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-dark pl-11"
            data-testid="search-sales"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="input-dark w-40" data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="all" className="text-white">Todos (Estado)</SelectItem>
            <SelectItem value="paid" className="text-white">Pagos</SelectItem>
            <SelectItem value="pending" className="text-white">Pendentes</SelectItem>
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="input-dark w-48 flex items-center justify-between h-10 px-3 rounded-md text-sm"
              data-testid="filter-room"
            >
              <span className={filterRoom.size === 0 ? "text-zinc-400" : "text-white truncate"}>
                {filterRoom.size === 0
                  ? "Todas (Sala)"
                  : filterRoom.size === 1
                    ? (roomOptions.find((r) => r.id === Array.from(filterRoom)[0])?.name || "1 sala")
                    : `${filterRoom.size} salas`}
              </span>
              <ChevronDown className="w-4 h-4 opacity-60 flex-shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="bg-zinc-900 border-white/10 w-64 p-2" align="start">
            <div className="flex items-center justify-between px-1 pb-2 border-b border-white/10 mb-2">
              <span className="text-xs text-zinc-500">Seleciona uma ou mais salas</span>
              {filterRoom.size > 0 && (
                <button
                  type="button"
                  onClick={() => setFilterRoom(new Set())}
                  className="text-xs text-amber-400 hover:text-amber-300"
                  data-testid="filter-room-clear"
                >
                  Limpar
                </button>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {roomOptions.length === 0 && (
                <p className="text-zinc-500 text-xs px-2 py-2">Sem salas configuradas</p>
              )}
              {roomOptions.map((r) => {
                const checked = filterRoom.has(r.id);
                return (
                  <label
                    key={r.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/5 cursor-pointer"
                    data-testid={`filter-room-option-${r.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => {
                        setFilterRoom((prev) => {
                          const next = new Set(prev);
                          if (v) next.add(r.id); else next.delete(r.id);
                          return next;
                        });
                      }}
                    />
                    <span className="flex-1 text-white text-sm truncate">{r.name}</span>
                    <span className="text-zinc-500 text-xs">· {r.count}</span>
                  </label>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        <Select value={filterSession} onValueChange={setFilterSession}>
          <SelectTrigger className="input-dark w-56" data-testid="filter-session">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10 max-h-72">
            <SelectItem value="all" className="text-white">Todas (Sessão)</SelectItem>
            {sessionOptions.map((s) => (
              <SelectItem key={s.id} value={s.id} className="text-white">
                {s.label} <span className="text-zinc-500">· {s.count}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterTicketType} onValueChange={setFilterTicketType}>
          <SelectTrigger className="input-dark w-56" data-testid="filter-ticket-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10 max-h-72">
            <SelectItem value="all" className="text-white">Todos (Tipo de Bilhete)</SelectItem>
            {distinctTicketTypes.map((t) => (
              <SelectItem key={t.id || `name:${t.name}`} value={t.id ? `id:${t.id}` : `name:${t.name}`} className="text-white">
                {t.name} <span className="text-zinc-500">· {t.count}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterPlan} onValueChange={setFilterPlan}>
          <SelectTrigger className="input-dark w-44" data-testid="filter-plan">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="all" className="text-white">Todos (Plano)</SelectItem>
            <SelectItem value="plan_yes" className="text-white">Apenas planos</SelectItem>
            <SelectItem value="plan_no" className="text-white">Sem plano</SelectItem>
            <SelectItem value="active" className="text-white">Plano em curso</SelectItem>
            <SelectItem value="overdue" className="text-white">Plano em atraso</SelectItem>
            <SelectItem value="completed" className="text-white">Plano concluído</SelectItem>
            <SelectItem value="cancelled" className="text-white">Plano cancelado</SelectItem>
            <SelectItem value="frozen" className="text-white">Plano congelado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterMinor} onValueChange={setFilterMinor}>
          <SelectTrigger className="input-dark w-44" data-testid="filter-minor">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="all" className="text-white">Todos (Menor)</SelectItem>
            <SelectItem value="minor_yes" className="text-white">Apenas menores</SelectItem>
            <SelectItem value="minor_no" className="text-white">Sem termo parental</SelectItem>
            <SelectItem value="child_under_12" className="text-white">👶 Crianças ≤12</SelectItem>
            <SelectItem value="teen_13_17" className="text-white">🧒 Jovens 13-17</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-x-auto">
        <table className="table-dark min-w-[1000px]">
          <thead>
            <tr>
              {colVisible("actions") && <th>AÇÕES</th>}
              {colVisible("status") && <SortableHeader label="STATUS" sortKey="payment_status" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("ticket_number") && <SortableHeader label="CÓDIGO_BILHETE" sortKey="ticket_number" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("created_at") && <SortableHeader label="DATA_COMPRA" sortKey="created_at" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("first_name") && <SortableHeader label="PRIMEIRO_NOME" sortKey="first_name" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("last_name") && <SortableHeader label="ULTIMO_NOME" sortKey="last_name" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("email") && <SortableHeader label="EMAIL" sortKey="email" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("phone") && <SortableHeader label="TELEFONE" sortKey="phone" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("church") && <SortableHeader label="IGREJA" sortKey="church" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("age") && <SortableHeader label="IDADE" sortKey="age" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("group_code") && <SortableHeader label="GRUPO" sortKey="group_code" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("vip") && <SortableHeader label="VIP" sortKey="is_vip" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("session_date") && <SortableHeader label="SESSAO" sortKey="session_date" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("ticket_type_name") && <SortableHeader label="TIPO_BILHETE" sortKey="ticket_type_name" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("minor") && <SortableHeader label="MENOR" sortKey="parental_auth.minor.category" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("final_price") && <SortableHeader label="VALOR_A_COBRAR" sortKey="final_price" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("amount_paid") && <SortableHeader label="VALOR_PAGO" sortKey="amount_paid" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("amount_due") && <SortableHeader label="VALOR_EM_FALTA" sortKey="amount_due" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("payment_method") && <SortableHeader label="TIPO_PAGAMENTO" sortKey="payment_method" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("plan") && <SortableHeader label="PLANO" sortKey="plan_status" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("coupon_code") && <SortableHeader label="CUPAO" sortKey="coupon_code" currentSort={sortConfig} onSort={handleSort} />}
              {colVisible("checked_in") && <SortableHeader label="CHECKIN" sortKey="checked_in" currentSort={sortConfig} onSort={handleSort} />}
              {nonCoreCustomFields.map((cf) => colVisible(`cf_${cf.name}`) && (
                <th key={`th-cf-${cf.id}`} className="text-violet-200 whitespace-nowrap uppercase" title="Campo personalizado">
                  {cf.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedSales.map((sale) => (
              <tr key={sale.id} data-testid={`sale-row-${sale.id}`}>
                {colVisible("actions") && <td>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                      onClick={() => {
                        const phone = (sale.phone || "").replace(/\D/g, "");
                        if (!phone) { toast.error("Este bilhete não tem número de telefone"); return; }
                        const name = sale.name || "";
                        const msg = encodeURIComponent(`Olá ${name}, contactamos sobre o seu bilhete ${sale.ticket_number}.`);
                        window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
                      }}
                      title="Enviar WhatsApp"
                      data-testid={`whatsapp-sale-${sale.id}`}
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                      onClick={() => handleDownloadTicketPDF(sale)}
                      title="Baixar PDF do Bilhete"
                      data-testid={`download-ticket-${sale.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                      onClick={async () => {
                        try {
                          await axios.post(`${API_URL}/api/admin/sales/${sale.id}/resend-ticket`, {}, { withCredentials: true });
                          toast.success(`Bilhete reenviado para ${sale.email}`);
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Erro ao reenviar bilhete");
                        }
                      }}
                      title="Reenviar Bilhete por Email"
                      data-testid={`resend-ticket-${sale.id}`}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
                      onClick={() => setEditingSale(sale)}
                      data-testid={`edit-sale-${sale.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-zinc-900 border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Eliminar Venda</AlertDialogTitle>
                          <AlertDialogDescription className="text-zinc-400">
                            Tem a certeza que deseja eliminar esta venda? Esta ação não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteSale(sale.id)}
                            className="bg-red-500 text-white hover:bg-red-600"
                            data-testid={`delete-sale-${sale.id}`}
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>}
                {colVisible("status") && <td>
                  <span className={`badge ${sale.payment_status === "paid" ? "badge-success" : "badge-warning"}`}>
                    {sale.payment_status === "paid" ? "Pago" : "Pendente"}
                  </span>
                </td>}
                {colVisible("ticket_number") && <td className="font-mono text-blue-500">{sale.ticket_number}</td>}
                {colVisible("created_at") && <td className="text-zinc-400 text-sm whitespace-nowrap" data-testid={`sale-created-${sale.id}`}>
                  {sale.created_at
                    ? new Date(sale.created_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })
                    : "—"}
                </td>}
                {colVisible("first_name") && <td className="text-white">{sale.first_name}</td>}
                {colVisible("last_name") && <td className="text-white">{sale.last_name}</td>}
                {colVisible("email") && <td className="text-zinc-400">{sale.email}</td>}
                {colVisible("phone") && <td className="text-zinc-400">{sale.phone}</td>}
                {colVisible("church") && <td className="text-zinc-400">{sale.church}</td>}
                {colVisible("age") && <td className="text-zinc-400">{sale.age}</td>}
                {colVisible("group_code") && <td>
                  {sale.group_code ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 font-mono">
                      {sale.group_code}
                    </span>
                  ) : (
                    <span className="text-zinc-600">-</span>
                  )}
                </td>}
                {colVisible("vip") && <td>
                  {sale.is_vip ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                      ⭐ VIP
                    </span>
                  ) : (
                    <span className="text-zinc-600">-</span>
                  )}
                </td>}
                {colVisible("session_date") && <td className="text-zinc-400">{sale.session_date} {sale.session_time}</td>}
                {colVisible("ticket_type_name") && <td>
                  {sale.ticket_type_name ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                      {sale.ticket_type_name}
                    </span>
                  ) : (
                    <span className="text-zinc-600">-</span>
                  )}
                </td>}
                {colVisible("minor") && <td data-testid={`sale-minor-${sale.id}`}>
                  {sale.parental_auth ? (() => {
                    const m = sale.parental_auth.minor || {};
                    const r = sale.parental_auth.responsible || {};
                    const isU12 = m.category === "child_under_12";
                    const cls = isU12
                      ? "bg-pink-500/15 text-pink-200 border-pink-500/30 hover:bg-pink-500/25"
                      : "bg-cyan-500/15 text-cyan-200 border-cyan-500/30 hover:bg-cyan-500/25";
                    const icon = isU12 ? "👶" : "🧒";
                    return (
                      <button
                        type="button"
                        onClick={() => setMinorSale(sale)}
                        className="flex flex-col gap-1 min-w-[150px] text-left cursor-pointer"
                        title="Clique para ver termo de responsabilidade"
                        data-testid={`sale-minor-btn-${sale.id}`}
                      >
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium w-fit transition-colors ${cls}`}>
                          {icon} {isU12 ? "≤12" : "13-17"} · {m.age != null ? `${m.age}a` : "?"}
                        </span>
                        <span className="text-[10px] text-zinc-300 truncate max-w-[150px]">{m.full_name || "—"}</span>
                        {r.full_name && (
                          <span className="text-[10px] text-zinc-500 truncate max-w-[150px]">
                            c/ {r.full_name}
                          </span>
                        )}
                      </button>
                    );
                  })() : (
                    <span className="text-zinc-600 text-xs">—</span>
                  )}
                </td>}
                {colVisible("final_price") && <td className="text-white font-medium">{sale.final_price != null ? `${sale.final_price.toFixed(2)}€` : "-"}</td>}
                {colVisible("amount_paid") && <td className={`font-medium ${(() => { const paid = sale.amount_paid != null ? Number(sale.amount_paid) : (sale.payment_status === "paid" ? (sale.final_price || 0) : 0); return paid === (sale.final_price || 0) ? "text-green-400" : "text-yellow-500"; })()}`}>{sale.amount_paid != null ? `${Number(sale.amount_paid).toFixed(2)}€` : (sale.payment_status === "paid" ? `${(sale.final_price || 0).toFixed(2)}€` : "0.00€")}</td>}
                {colVisible("amount_due") && <td className={`font-medium ${(() => { const paid = sale.amount_paid != null ? Number(sale.amount_paid) : (sale.payment_status === "paid" ? (sale.final_price || 0) : 0); const due = Math.max(0, (sale.final_price || 0) - paid); return due > 0 ? "text-red-400" : "text-green-400"; })()}`}>{(() => { const paid = sale.amount_paid != null ? Number(sale.amount_paid) : (sale.payment_status === "paid" ? (sale.final_price || 0) : 0); return `${Math.max(0, (sale.final_price || 0) - paid).toFixed(2)}€`; })()}</td>}
                {colVisible("payment_method") && <td>
                  {sale.payment_method ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">{sale.payment_method}</span>
                  ) : (
                    <span className="text-zinc-600">-</span>
                  )}
                </td>}
                {colVisible("plan") && <td data-testid={`sale-plan-${sale.id}`}>
                  {sale.is_installment_plan ? (() => {
                    const due = Number(sale.total_due || sale.final_price || 0);
                    const paid = Number(sale.total_paid || 0);
                    const percent = due > 0 ? Math.min(100, Math.round((paid / due) * 100)) : 0;
                    const status = sale.plan_status || "active";
                    const meta = {
                      active:    { lbl: "Em curso",   cls: "bg-amber-500/15 text-amber-300 border-amber-500/30 hover:bg-amber-500/25"   },
                      completed: { lbl: "Concluído",  cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25" },
                      cancelled: { lbl: "Cancelado",  cls: "bg-red-500/15 text-red-300 border-red-500/30 hover:bg-red-500/25"           },
                      overdue:   { lbl: "Em atraso",  cls: "bg-orange-500/15 text-orange-300 border-orange-500/30 hover:bg-orange-500/25" },
                      frozen:    { lbl: "Congelado",  cls: "bg-sky-500/15 text-sky-300 border-sky-500/30 hover:bg-sky-500/25"           },
                      expired:   { lbl: "Expirado",   cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30 hover:bg-zinc-500/25"        },
                    }[status] || { lbl: status, cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" };
                    const count = (sale.installments || []).length;
                    const openRequests = (sale.schedule_change_requests || []).filter(
                      (r) => r.status === "pending" || r.status === "countered",
                    ).length;
                    const hasSchedule = (sale.payment_schedule || []).length > 0;
                    return (
                      <div className="flex flex-col gap-1 min-w-[140px]">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setPlanHistorySale(sale)}
                            className="cursor-pointer text-left"
                            title="Ver histórico rápido"
                            data-testid={`sale-plan-btn-${sale.id}`}
                          >
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium transition-colors ${meta.cls}`}>
                              {meta.lbl} · {percent}%
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/plans?open=${sale.id}`); }}
                            className="text-sky-400 hover:text-sky-200 p-0.5 rounded hover:bg-sky-500/10 transition-colors"
                            title="Abrir na Gestão de Planos"
                            data-testid={`sale-plan-link-${sale.id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <span className="text-[10px] text-zinc-500 font-mono">
                          {paid.toFixed(2)}€ / {due.toFixed(2)}€
                          {count > 0 && <span className="ml-1 text-zinc-400">· {count}× </span>}
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {hasSchedule && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/20" title="Cronograma detalhado acordado">
                              📋 {(sale.payment_schedule || []).length}× prest.
                            </span>
                          )}
                          {sale.payment_agreement_digital_signature && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20" title="Cronograma assinado digitalmente">
                              ✓ Assinado
                            </span>
                          )}
                          {openRequests > 0 && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-medium" title="Pedidos de alteração pendentes">
                              ⏳ {openRequests}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })() : (
                    <span className="text-zinc-600 text-xs">—</span>
                  )}
                </td>}
                {colVisible("coupon_code") && <td>
                  {sale.coupon_code ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20 font-mono">
                      {sale.coupon_code}
                    </span>
                  ) : (
                    <span className="text-zinc-600">-</span>
                  )}
                </td>}
                {colVisible("checked_in") && <td>
                  <span className={`badge ${sale.checked_in ? "badge-success" : "badge-warning"}`}>
                    {sale.checked_in ? "Sim" : "Não"}
                  </span>
                </td>}
                {nonCoreCustomFields.map((cf) => colVisible(`cf_${cf.name}`) && (
                  <td key={`td-cf-${cf.id}-${sale.id}`} className="text-zinc-300 text-sm whitespace-nowrap">
                    {(() => {
                      const v = sale.custom_fields?.[cf.name];
                      if (v === undefined || v === null || v === "") return <span className="text-zinc-600">—</span>;
                      if (cf.field_type === "checkbox") return v ? "✓" : "—";
                      return String(v);
                    })()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {filteredAndSortedSales.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            Nenhuma venda encontrada
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <EditSaleModal
        sale={editingSale}
        sessions={sessions}
        showData={showData}
        isOpen={!!editingSale}
        onClose={() => setEditingSale(null)}
        onSave={fetchData}
      />

      {/* Histórico de Prestações */}
      <PlanHistoryDialog
        sale={planHistorySale}
        onClose={() => setPlanHistorySale(null)}
        accentColor={showData?.accent_color || "#f59e0b"}
      />

      {/* Termo Parental */}
      <MinorParentalDialog
        sale={minorSale}
        onClose={() => setMinorSale(null)}
        onRefresh={async (updatedReg) => {
          if (updatedReg) setMinorSale(updatedReg);
          await fetchData();
        }}
      />

      {/* PDF Column Picker Modal */}
      <ColumnPickerModal
        open={showPdfPicker}
        onClose={() => setShowPdfPicker(false)}
        columns={columns}
        storageKey="pdf-cols-bilhetes"
        title="Exportar PDF · Gestão de Bilhetes"
        accentColor={showData?.accent_color || "#f59e0b"}
        onConfirm={(cols) => exportToPDF(exportData, cols, "Gestão de Bilhetes", "bilhetes")}
      />

      {/* Table Column Visibility Picker */}
      <ColumnPickerModal
        open={showColsPicker}
        onClose={() => setShowColsPicker(false)}
        columns={[
          { key: "actions", header: "AÇÕES" },
          { key: "status", header: "STATUS" },
          { key: "ticket_number", header: "CÓDIGO_BILHETE" },
          { key: "created_at", header: "DATA_COMPRA" },
          { key: "first_name", header: "PRIMEIRO_NOME" },
          { key: "last_name", header: "ULTIMO_NOME" },
          { key: "email", header: "EMAIL" },
          { key: "phone", header: "TELEFONE" },
          { key: "church", header: "IGREJA" },
          { key: "age", header: "IDADE" },
          { key: "group_code", header: "GRUPO" },
          { key: "vip", header: "VIP" },
          { key: "session_date", header: "SESSAO" },
          { key: "ticket_type_name", header: "TIPO_BILHETE" },
          { key: "minor", header: "MENOR" },
          { key: "final_price", header: "VALOR_A_COBRAR" },
          { key: "amount_paid", header: "VALOR_PAGO" },
          { key: "amount_due", header: "VALOR_EM_FALTA" },
          { key: "payment_method", header: "TIPO_PAGAMENTO" },
          { key: "plan", header: "PLANO" },
          { key: "coupon_code", header: "CUPAO" },
          { key: "checked_in", header: "CHECKIN" },
          ...nonCoreCustomFields.map((cf) => ({ key: `cf_${cf.name}`, header: `${cf.label.toUpperCase()} (CAMPO PERSONALIZADO)` })),
        ]}
        storageKey={SALES_COLS_STORAGE}
        title="Colunas visíveis · Gestão de Bilhetes"
        accentColor={showData?.accent_color || "#f59e0b"}
        confirmLabel="Aplicar"
        onConfirm={(cols) => { setVisibleCols(new Set(cols.map((c) => c.key))); setShowColsPicker(false); }}
      />
    </div>
  );
};

// Buyers List
